import React from 'react';

import { Box, Button, Input, Loader, Stack, TFText } from '@tf/ui';
import { fmt } from '@tf/utils';

import type { CRACalculation } from '../../../api/craCalculation';
import { useCRACalculationJobs } from '../../../api/craCalculation';
import { isJobRunning } from '../../../api/jobUtils';
import { useSegmentContext } from '../../../hooks';
import type { DefaultInputProps } from '../types';

import { DownloadLink } from './FileInput/DownloadLink';

const getCalculationResult = (job: CRACalculation, kind: 'template' | 'document') => {
	const objectId = job.data.result?.[kind]?.job_attachment._object;
	const file = job.attachments.find((a) => a.object.id === objectId)?.object;

	if (!file) {
		return null;
	}

	const dateWithExtension = file.meta['@filename'].split('cra_result_')[1] ?? '';
	const [date, extension] = dateWithExtension.split('.');

	return {
		...file,
		meta: {
			...file.meta,
			'@filename': date
				? `CRA result of ${fmt.toDate(date, { preset: 'full_date' })}.${extension}`
				: file.meta['@filename'],
		},
	};
};

export const CRACalculationJobInfo: React.FC<DefaultInputProps> = ({ label, isReadOnly }) => {
	const {
		identity: { graphId: profileNodeId },
	} = useSegmentContext();

	const segmentJobs = useCRACalculationJobs(profileNodeId);

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Input.Label>{label}</Input.Label>
				{!isReadOnly && (
					<Button
						size="sm"
						variant="light"
						loading={segmentJobs.isCreating}
						onClick={() => segmentJobs.createJob()}
					>
						Calculate risk rating
					</Button>
				)}
			</Box>

			{segmentJobs.data && segmentJobs.data?.length > 0 ? (
				<Stack mt="0.5rem" gap="0.25rem">
					{segmentJobs.data.map((job) => {
						const documentFile = getCalculationResult(job, 'document');
						const templateFile = getCalculationResult(job, 'template');

						return (
							<Box
								key={job.id}
								sx={{ padding: '0.5rem', border: '1px solid #CCC', borderRadius: '0.25rem' }}
							>
								<Stack gap="0.125rem">
									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<div>
											Risk rating:{' '}
											{isJobRunning(job) ? (
												<Box sx={{ display: 'inline-block', verticalAlign: 'middle' }}>
													<Loader size={14} />
												</Box>
											) : (
												job.data.result?.risk_level || 'N/A'
											)}
										</div>
										<TFText fw={400} c="dimmed">
											{fmt.toDate(job.meta['@createdAt'], { preset: 'full_date_with_time' })}
										</TFText>
									</Box>

									<Box>Risk remarks: {job.data.result?.risk_remark ?? ''}</Box>

									{documentFile ? (
										<Box>
											Calculation: <DownloadLink file={documentFile} />
										</Box>
									) : null}

									{/* The default template has no filename, don't show it */}
									{templateFile && templateFile.meta['@filename'] ? (
										<Box>
											CRA template: <DownloadLink file={templateFile} />
										</Box>
									) : null}
								</Stack>
							</Box>
						);
					})}
				</Stack>
			) : segmentJobs.data?.length === 0 ? (
				<TFText c="dimmed">No previous risk rating available</TFText>
			) : segmentJobs.isError ? (
				<TFText c="dimmed">Failed to load calculations, please try again later</TFText>
			) : (
				<Box p="0.5rem">
					<Loader size={18} />
				</Box>
			)}
		</Box>
	);
};
